import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Send,
  RefreshCw,
  Database,
  Cloud,
  Brain,
  MessageSquare,
  Phone,
  Key,
  Loader2
} from 'lucide-react';

interface EnvStatus {
  status: string;
  smsReady: boolean;
  twilioNumber: string;
  environment: Record<string, string>;
  bindings: {
    database: boolean;
    storage: boolean;
    cache: boolean;
    ai: boolean;
  };
  timestamp: string;
}

export default function DevDashboard() {
  const [envStatus, setEnvStatus] = useState<EnvStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Test from Dev Dashboard');
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const fetchEnv = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/dev/env');
      const data = await response.json();
      setEnvStatus(data);
    } catch (error) {
      console.error('Failed to fetch env:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnv();
  }, []);

  const sendTestSMS = async () => {
    if (!testNumber) return;
    
    setSending(true);
    setTestResult(null);
    
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/dev/test-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: testNumber,
          message: testMessage
        })
      });
      
      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Network error' 
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!envStatus) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-xl">Failed to load environment variables</p>
          <button onClick={fetchEnv} className="mt-4 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-blue-400">🔧 Dev Dashboard</h1>
          <p className="text-gray-400">Hardcore mode - showing all environment variables</p>
          <div className="mt-4 flex items-center gap-4">
            <span className={`px-3 py-1 rounded ${envStatus.smsReady ? 'bg-green-600' : 'bg-red-600'}`}>
              SMS: {envStatus.smsReady ? 'READY' : 'NOT CONFIGURED'}
            </span>
            <span className="text-sm text-gray-400">Phone: {envStatus.twilioNumber}</span>
            <button
              onClick={fetchEnv}
              className="ml-auto px-3 py-1 bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Bindings Status */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className={`p-4 rounded-lg ${envStatus.bindings.database ? 'bg-green-900' : 'bg-red-900'}`}>
            <Database className="h-6 w-6 mb-2" />
            <p className="font-semibold">Database (D1)</p>
            <p className="text-sm">{envStatus.bindings.database ? 'Connected' : 'Not Connected'}</p>
          </div>
          <div className={`p-4 rounded-lg ${envStatus.bindings.storage ? 'bg-green-900' : 'bg-red-900'}`}>
            <Cloud className="h-6 w-6 mb-2" />
            <p className="font-semibold">Storage (R2)</p>
            <p className="text-sm">{envStatus.bindings.storage ? 'Connected' : 'Not Connected'}</p>
          </div>
          <div className={`p-4 rounded-lg ${envStatus.bindings.cache ? 'bg-green-900' : 'bg-red-900'}`}>
            <Database className="h-6 w-6 mb-2" />
            <p className="font-semibold">Cache (KV)</p>
            <p className="text-sm">{envStatus.bindings.cache ? 'Connected' : 'Not Connected'}</p>
          </div>
          <div className={`p-4 rounded-lg ${envStatus.bindings.ai ? 'bg-green-900' : 'bg-red-900'}`}>
            <Brain className="h-6 w-6 mb-2" />
            <p className="font-semibold">AI</p>
            <p className="text-sm">{envStatus.bindings.ai ? 'Connected' : 'Not Connected'}</p>
          </div>
        </div>

        {/* Environment Variables */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-blue-400">Environment Variables</h2>
            <button
              onClick={() => setShowSecrets(!showSecrets)}
              className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 flex items-center gap-2"
            >
              {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showSecrets ? 'Hide' : 'Show'} Secrets
            </button>
          </div>
          
          <div className="space-y-2">
            {Object.entries(envStatus.environment).map(([key, value]) => {
              const isSet = value !== 'NOT SET';
              const isSecret = key.includes('TOKEN') || key.includes('SECRET') || key.includes('KEY');
              const displayValue = isSecret && !showSecrets && isSet ? '••••••••' : value;
              
              return (
                <div key={key} className="flex items-center justify-between py-2 px-3 bg-gray-700 rounded">
                  <div className="flex items-center gap-3">
                    {key.includes('TWILIO') && <Phone className="h-4 w-4 text-green-400" />}
                    {key.includes('WA_') && <MessageSquare className="h-4 w-4 text-green-400" />}
                    {key.includes('STRIPE') && <Key className="h-4 w-4 text-purple-400" />}
                    {key === 'DB' && <Database className="h-4 w-4 text-blue-400" />}
                    {key === 'AI' && <Brain className="h-4 w-4 text-yellow-400" />}
                    <span className="font-mono text-sm">{key}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-sm ${isSet ? 'text-green-400' : 'text-red-400'}`}>
                      {displayValue}
                    </span>
                    {isSet ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Test SMS */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-blue-400 mb-4">Test SMS Sending</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Phone Number</label>
              <input
                type="tel"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="+15555551234"
                className="w-full px-3 py-2 bg-gray-700 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Message</label>
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <button
            onClick={sendTestSMS}
            disabled={!testNumber || sending || !envStatus.smsReady}
            className={`px-4 py-2 rounded font-semibold flex items-center gap-2 ${
              envStatus.smsReady 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-gray-600 cursor-not-allowed'
            }`}
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Test SMS
              </>
            )}
          </button>
          
          {testResult && (
            <div className={`mt-4 p-4 rounded ${
              testResult.success ? 'bg-green-900' : 'bg-red-900'
            }`}>
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          Last updated: {new Date(envStatus.timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
}